import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Clock } from 'lucide-react';

function getWeekRange() {
  const d = new Date();
  const day = d.getDay();
  const mon = new Date(d);
  mon.setDate(d.getDate() - ((day + 6) % 7));
  const sun = new Date(mon);
  sun.setDate(mon.getDate() + 6);
  return { from: mon.toISOString().split('T')[0], to: sun.toISOString().split('T')[0] };
}

function getMonthRange() {
  const d = new Date();
  const first = new Date(d.getFullYear(), d.getMonth(), 1);
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return { from: first.toISOString().split('T')[0], to: last.toISOString().split('T')[0] };
}

export default function Hours() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [dateFrom, setDateFrom] = useState(getWeekRange().from);
  const [dateTo, setDateTo] = useState(getWeekRange().to);
  const [tab, setTab] = useState('semanal');

  const load = useCallback(async () => {
    try {
      const res = await API.get(`/hours?date_from=${dateFrom}&date_to=${dateTo}`);
      setData(res.data);
    } catch { toast.error('Error cargando horas'); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const handleTabChange = (v) => {
    setTab(v);
    if (v === 'semanal') {
      const r = getWeekRange();
      setDateFrom(r.from);
      setDateTo(r.to);
    } else {
      const r = getMonthRange();
      setDateFrom(r.from);
      setDateTo(r.to);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="hours-page">
      <div>
        <h1 className="font-heading text-2xl font-bold text-zinc-50">Mis Horas</h1>
        <p className="text-sm text-zinc-500">Consulta tus horas trabajadas</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
        <Tabs value={tab} onValueChange={handleTabChange}>
          <TabsList className="bg-zinc-900">
            <TabsTrigger value="semanal" data-testid="hours-tab-weekly">Semanal</TabsTrigger>
            <TabsTrigger value="mensual" data-testid="hours-tab-monthly">Mensual</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex gap-2 items-center">
          <div className="space-y-1">
            <Label className="text-xs text-zinc-500">Desde</Label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="bg-zinc-950/50 border-white/10 h-9 text-sm" aria-label="Fecha desde" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-zinc-500">Hasta</Label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="bg-zinc-950/50 border-white/10 h-9 text-sm" aria-label="Fecha hasta" />
          </div>
        </div>
      </div>

      <Card className="glass-card">
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-base text-zinc-200">Resumen</CardTitle>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-amber-500" />
            <span className="text-2xl font-heading font-bold text-amber-500" data-testid="hours-total">{data?.total_hours ?? '-'}h</span>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-zinc-500">Periodo: {dateFrom} a {dateTo}</p>
        </CardContent>
      </Card>

      <div className="rounded-lg border border-white/5 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead className="text-zinc-400">Fecha</TableHead>
              <TableHead className="text-zinc-400">Inicio</TableHead>
              <TableHead className="text-zinc-400">Fin</TableHead>
              <TableHead className="text-zinc-400 hidden sm:table-cell">Puesto</TableHead>
              <TableHead className="text-zinc-400 text-right">Horas</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(!data || data.shifts?.length === 0) && (
              <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-8">Sin turnos en este periodo</TableCell></TableRow>
            )}
            {data?.shifts?.map(s => (
              <TableRow key={s.id} className="border-white/5" data-testid={`hours-row-${s.id}`}>
                <TableCell className="text-zinc-200">{new Date(s.date + 'T00:00:00').toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' })}</TableCell>
                <TableCell className="text-zinc-400">{s.start_time}</TableCell>
                <TableCell className="text-zinc-400">{s.end_time}</TableCell>
                <TableCell className="hidden sm:table-cell">
                  {s.role_tag && <Badge variant="outline" className="text-xs text-zinc-500 border-white/5">{s.role_tag}</Badge>}
                </TableCell>
                <TableCell className="text-right font-medium text-amber-500/80">{s.hours}h</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
